/* mv6-purchase.js — the v6 PDP's money path.
 *
 * Owns: pricing arithmetic, the Shopify cart, quantity rules. Owns no DOM.
 * The section's UI subscribes to `mv6.on('change', fn)` and renders from
 * `mv6.state()`; nothing in the UI layer may do price math of its own.
 *
 * Deliberate departure from `.claude/skills/shopify/purchase-module`: v6 does
 * NOT use the visual-proxy pattern (hidden Seal widget, hidden ATC button,
 * `.sls-option-container` syncing, the 100ms click). That pattern exists so a
 * page can borrow the theme's product form. v6 has its own cart drawer and no
 * theme form, and Seal's plans are native Shopify selling plans, so
 * /cart/add.js accepts `selling_plan` directly. Dropping the proxy removes a
 * dependency on Seal's internal class names, the `product-form__submit`
 * hazard, and a full page navigation to /cart.
 *
 * Two rules from that skill still bind and are honoured here:
 *   1. Never hardcode a plan price — read `per_delivery_price` (via the
 *      Liquid snippet, which reads the allocation).
 *   2. Always compute a discount percentage from the actual price, never
 *      inherit one and add an offset.
 */
(function () {
  'use strict';

  var DATA = (function () {
    var el = document.getElementById('mv6-product');
    if (!el) { console.error('[mv6] #mv6-product missing — render mv6-product-data.liquid first.'); return null; }
    try { return JSON.parse(el.textContent); } catch (e) { console.error('[mv6] product JSON unparseable', e); return null; }
  })();

  if (!DATA) return;

  if (DATA.legacy_plans_present) {
    console.info('[mv6] ' + DATA.legacy_plans_present + ' legacy selling plan(s) on this variant — ignored by design (rule 182786 holds existing contracts).');
  }

  /* ── R-101 · THE MONEY PATH FAILS CLOSED ───────────────────────────────
   * mv6-product-data.liquid resolves the variant by id → SKU and reports the
   * outcome as `variant_resolved`. Until 15 Aug 2026 NOTHING READ IT. What it
   * falls back to is `selected_or_first_available_variant`, which on this
   * product is a Bundle Deal variant — so an unresolved variant was still
   * priced by this file and still added to the cart, one console error away
   * from selling a different purchase model at the wrong price. Found by an
   * outside review, not by any gate: every gate we own proves a property we
   * already thought to check.
   *
   * An explicit `false` is authoritative and disables buying. A MISSING key is
   * not — dev/mv6-harness.html's fixture predates both fields, and a dev
   * harness that cannot add to cart is a harness nobody runs. Only what the
   * theme actually emits can block.
   *
   * Removing a line and emptying the cart stay available in every state: a
   * customer must always be able to undo, whatever is wrong with the page. */
  var SELLABLE = DATA.variant_resolved !== false && DATA.available !== false;
  var BLOCKED_MSG = DATA.available === false
    ? 'This item is out of stock right now. Nothing has been added to your cart.'
    : 'We can’t add this to your cart right now. Please refresh the page, or contact support if it keeps happening.';
  if (!SELLABLE) {
    console.error('[mv6] PURCHASING DISABLED — variant_resolved:', DATA.variant_resolved,
      '· available:', DATA.available, '· variant:', DATA.variant_id, DATA.variant_title,
      '\n  v6 sells the single-bottle variant only. Fix the variant in Shopify; do not "unblock" this.');
  }

  /* ── Pricing ──────────────────────────────────────────────────────────
   * One-time is a per-UNIT table, because Shopify's automatic discounts are
   * fixed per-item amounts allocated per unit and truncated. Line-level math
   * (total × 0.8) lands a cent off at three bottles. Subscribe is also a
   * per-unit table, read from the Seal allocation — never the plan price with
   * a quantity bonus multiplied on top. Multiplying a 5% bonus onto a 30%
   * plan price lands on 33.5%, and the drawer then reads "34% OFF" beside a
   * "save 5%" chip on a 30% offer. Both ladders are ADDITIVE against list:
   * one-time 0/15/20, subscribe 30/35/40.
   */
  function otUnit(qty) {
    if (qty >= 3) return DATA.onetime['3'];
    if (qty === 2) return DATA.onetime['2'];
    return DATA.onetime['1'];
  }

  function subUnit(qty) {
    var tier = qty >= 3 ? '3' : qty === 2 ? '2' : '1';
    var p = DATA.plans[tier] && DATA.plans[tier].price;
    // Fall back to tier 1 rather than to a literal: a literal would outlive a
    // price change in the admin, which is the whole failure mode this avoids.
    return p || (DATA.plans['1'] && DATA.plans['1'].price) || DATA.price;
  }

  function planId(qty) {
    var tier = qty >= 3 ? '3' : qty === 2 ? '2' : '1';
    /* Cadence-aware (22 Aug 2026, drawer picker round). The 4-week column stays
       in DATA.plans (it carries the prices); 6/8 come from DATA.cadences. An
       unknown tier×cadence combination returns null and the money path fails
       CLOSED (R-101) — it must never fall back to a different cadence. */
    if (state.cadence && state.cadence !== '4') {
      var col = DATA.cadences && DATA.cadences[state.cadence];
      return (col && col[tier]) || null;
    }
    return (DATA.plans[tier] && DATA.plans[tier].id) || null;
  }

  function otTotal(qty) { return otUnit(qty) * qty; }
  function subTotal(qty) { return subUnit(qty) * qty; }

  function pct(from, to) {
    if (!from || to >= from) return 0;
    return Math.round(((from - to) / from) * 100);
  }

  /* ── Checkout Started event contract ─────────────────────────────────
   * Klaviyo's native Shopify Checkout Started event includes cart line-item
   * properties. Native selling-plan carts do not consistently include a plan
   * id there, so the flow cannot safely tell subscription from one-time by
   * price alone. These private Shopify properties make that distinction
   * explicit without showing metadata to the customer in cart or checkout.
   *
   * The leading underscore is Shopify's convention for private line-item
   * properties. Values are strings because Shopify serializes line properties
   * as text. `_evolance_event_contract` lets future flow revisions distinguish
   * this schema from older carts instead of guessing.
   */
  var EVENT_CONTRACT_VERSION = 'checkout_v1';
  var EVENT_PROPERTY_KEYS = [
    '_evolance_event_contract',
    '_evolance_purchase_type',
    '_evolance_bottle_count',
    '_evolance_selling_plan_id',
    '_evolance_savings_percent',
    '_evolance_billing_interval'
  ];

  function eventProperties(mode, qty, actual) {
    var isSub = mode === 'sub';
    var q = isSub ? capSub(qty) : capOt(qty);
    var total = actual && typeof actual.total === 'number'
      ? actual.total
      : (isSub ? subTotal(q) : otTotal(q));
    var actualPlan = actual && actual.planId != null ? actual.planId : null;
    var sellingPlan = isSub ? (actualPlan || planId(q)) : null;
    var base = DATA.compare_base * q;

    return {
      _evolance_event_contract: EVENT_CONTRACT_VERSION,
      _evolance_purchase_type: isSub ? 'subscription' : 'one_time',
      _evolance_bottle_count: String(q),
      _evolance_selling_plan_id: sellingPlan ? String(sellingPlan) : 'none',
      _evolance_savings_percent: String(pct(base, total)),
      _evolance_billing_interval: isSub ? 'every_' + (state.cadence || '4') + '_weeks' : 'none'
    };
  }

  function actualPlanId(line) {
    var allocation = line && line.selling_plan_allocation;
    return allocation && allocation.selling_plan && allocation.selling_plan.id
      ? allocation.selling_plan.id
      : null;
  }

  function isTargetLine(line) {
    return !!line && String(line.variant_id) === String(DATA.variant_id);
  }

  function expectedLineProperties(line) {
    var mode = line && line.selling_plan_allocation ? 'sub' : 'ot';
    return eventProperties(mode, line.quantity, {
      planId: actualPlanId(line),
      total: line.final_line_price
    });
  }

  function hasEventContract(line, expected) {
    var current = line.properties || {};
    for (var i = 0; i < EVENT_PROPERTY_KEYS.length; i += 1) {
      var key = EVENT_PROPERTY_KEYS[i];
      if (String(current[key] == null ? '' : current[key]) !== expected[key]) return false;
    }
    return true;
  }

  function mergeEventProperties(line, expected) {
    var merged = {};
    var current = line.properties || {};
    Object.keys(current).forEach(function (key) { merged[key] = current[key]; });
    EVENT_PROPERTY_KEYS.forEach(function (key) { merged[key] = expected[key]; });
    return merged;
  }

  function money(cents) {
    var n = (cents / 100).toFixed(2);
    var fmt = DATA.money_format || '${{amount}}';
    return fmt.indexOf('{{amount}}') > -1 ? fmt.replace('{{amount}}', n) : '$' + n;
  }

  /* ── Product facts the money path needs to talk in customer units ──────
   * One serving = two capsules = one night's dose. These live here, next to
   * the arithmetic, so a per-serving figure can never be computed in the UI
   * layer with a different divisor than the one the prices were built on.
   * If the bottle count ever changes, change it HERE and in the section's
   * `capsules_per_bottle` setting together.
   */
  var SERVINGS_PER_BOTTLE = 30;
  var CAPSULES_PER_BOTTLE = 60;

  /* Free shipping is earned by ANY subscription, or by a one-time cart over
   * this threshold. A shipping rule, not a price — but the drawer's progress
   * bar is arithmetic, so it belongs in this file rather than the UI. */
  var FREE_SHIP_CENTS = 6000;

  function perServing(unitCents) { return money(Math.round(unitCents / SERVINGS_PER_BOTTLE)); }

  /* ── View models ───────────────────────────────────────────────────────
   * Added Aug 2026 for the v6 Liquid section. The section renders text and
   * toggles attributes; every number it prints comes from here. Nothing
   * above this line changed — these are additive readers over the same
   * tables, so a price still has exactly one source.
   */
  function tierView(mode, q) {
    var isSub = mode === 'sub';
    var unit = isSub ? subUnit(q) : otUnit(q);
    var total = isSub ? subTotal(q) : otTotal(q);
    var base = DATA.compare_base * q;
    return {
      qty: q,
      unit: unit,
      total: total,
      compare: base,
      save: base - total,
      pct: pct(base, total),
      showCompare: base > total,
      per: perServing(unit),
      servings: q * SERVINGS_PER_BOTTLE,
      capsules: q * CAPSULES_PER_BOTTLE
    };
  }

  /* One entry per cart line. Prices come from Shopify's own line figures —
   * `final_line_price` already carries both the automatic quantity discount
   * and a selling plan's pricing policy, so nothing is re-derived. The only
   * computed figure is the saving, measured against LIST × qty, because that
   * is the comparison the PDP makes and checkout does not make it at all for
   * plan lines (documented in shopify-v6/README.md). */
  function lineViews() {
    var c = state.cart;
    if (!c || !c.items) return [];
    return c.items.map(function (it) {
      var isSub = !!it.selling_plan_allocation;
      var qty = it.quantity;
      var final = it.final_line_price;
      var base = DATA.compare_base * qty;
      return {
        key: it.key,
        item: it,
        isSub: isSub,
        qty: qty,
        unit: it.final_price,
        total: final,
        compare: base,
        save: base - final,
        hasDisc: base - final > 0,
        pct: pct(base, final),
        cap: isSub ? DATA.sub_max_qty : Infinity,
        atCap: isSub && qty >= DATA.sub_max_qty,
        servings: qty * SERVINGS_PER_BOTTLE,
        capsules: qty * CAPSULES_PER_BOTTLE
      };
    });
  }

  function splitLines() {
    var sub = null, ot = null;
    lineViews().forEach(function (l) { if (l.isSub) { if (!sub) sub = l; } else if (!ot) ot = l; });
    return { sub: sub, ot: ot };
  }

  function cartTotals() {
    var ls = lineViews();
    var bottles = 0, total = 0, base = 0, hasSub = false;
    ls.forEach(function (l) { bottles += l.qty; total += l.total; base += l.compare; if (l.isSub) hasSub = true; });
    var free = hasSub || total >= FREE_SHIP_CENTS;
    return {
      itemCount: state.cart ? state.cart.item_count : 0,
      bottles: bottles,
      total: total,
      compare: base,
      save: base - total,
      hasSave: base - total > 0,
      pct: pct(base, total),
      freeShipping: free,
      freeShipLeft: Math.max(0, FREE_SHIP_CENTS - total),
      freeShipPct: free ? 100 : Math.round((total / FREE_SHIP_CENTS) * 100),
      threshold: FREE_SHIP_CENTS
    };
  }

  /* The drawer's "Subscribe & save an extra $X" card.
   * The amount is (cart before) − (cart after), so it accounts for the
   * one-time line losing its own quantity discount AND the subscription line
   * stepping up a tier. It is DOLLARS, never a percentage — the one-time side
   * carries its own 0/15/20 ladder, so a percentage comes out 30.0 → 23.5 →
   * 25.0 and FALLS between one and two bottles.
   * `show` is gated so every figure the card ever displays is MONOTONIC: past
   * the 3-bottle ceiling only part of the cart can move and the exact amount
   * shrinks as the cart grows, which reads as a bug. We give up the upsell on
   * carts that already hold three or more bottles. */
  function upsellView() {
    var s = splitLines();
    var subQ = s.sub ? s.sub.qty : 0;
    var otQ = s.ot ? s.ot.qty : 0;
    var target = subQ + otQ;
    var show = otQ > 0 && subQ < DATA.sub_max_qty && target <= DATA.sub_max_qty;
    var before = (otQ ? otTotal(otQ) : 0) + (subQ ? subTotal(subQ) : 0);
    var after = target ? subTotal(target) : 0;
    return { show: show, amount: before - after, target: target, subQty: subQ, otQty: otQ };
  }

  /* ── Quantity rules ───────────────────────────────────────────────────
   * Subscription is hard-capped at sub_max_qty (3) because no fourth selling
   * plan exists — a 4-bottle subscription line would renew at the 3-bottle
   * rate forever. One-time is deliberately uncapped: the fixed $9.99/item
   * continues, so qty 4 and 5 stay at a steady 20% off.
   */
  function capSub(q) { return Math.max(1, Math.min(DATA.sub_max_qty, q | 0)); }
  function capOt(q) { return Math.max(1, q | 0); }

  /* ── State ────────────────────────────────────────────────────────────── */
  var state = {
    mode: 'sub',        // 'sub' | 'ot'
    qty: 1,             // the selected tier — BOTH modes price it (tiles restored 22 Aug 2026)
    cadence: '4',       // delivery interval in weeks ('4'|'6'|'8') — price NEVER depends on it
    cart: null,         // last /cart.js payload
    busy: false,
    error: null
  };

  /* THE SUBSCRIBE CARD ALWAYS PRICES ONE BOTTLE. The one-time card prices the
   * selected tier.
   *
   * Reversed 13 Aug 2026, on the user's call, and the reversal is worth
   * explaining because the old note said the opposite — "reported as a bug and
   * it is not". Both cards used to price the same quantity so the plan choice
   * was apples-to-apples: at three bottles you saw $89.94 against $119.94, and
   * reverting the unselected card to one bottle would have shown $89.94 beside
   * $49.97 and made one-time look cheaper.
   *
   * That rationale expired the same day, when the bottle tiles were hidden in
   * subscribe mode: switching to subscribe now RESETS the tier to one
   * (mv6-calm-rest.js, the data-mv6-pick handler). So while one-time is
   * selected at two bottles, the subscribe card was quoting $64.96 for a plan
   * that costs $34.98 the instant you click it — a price the customer cannot
   * buy, contradicted by the card's own click. Apples-to-apples only holds
   * while both sides are reachable, and one side no longer is.
   *
   * ── NOTHING WAS DELETED. HOW TO BRING SUBSCRIBE BOTTLE TIERS BACK ──────
   * The subscribe multi-bottle path is intact and hidden, not removed — the
   * plans, the price table, `capSub`, `planId(q)` and the tiles' own markup
   * (including tile 1's subscribe-only badge) are all still here. Restoring it
   * is two edits, and NEITHER is in this file:
   *   1. sections/mv6-calm-rest.liquid — the tiles' wrapper is
   *      `<div data-mv6-when="ot">`. Widen or drop that gate so the block shows
   *      in subscribe mode too.
   *   2. assets/mv6-calm-rest.js — in the `data-mv6-pick` handler, drop the
   *      `setQty(1)` reset that fires when the tiles go away.
   * This file then needs NO change beyond cards.sub pricing the chosen tier
   * (its interim `isSub ? q : 1` guard became `q` on 22 Aug 2026 — both cards
   * now price the selected tier, the original apples-to-apples behavior).
   * Whoever restores the tiles should also revisit the residual noted in
   * CLAUDE.md (one bottle beside two on the same screen) — it disappears on its
   * own once both sides are reachable again.
   *
   * ── RESTORED, 22 Aug 2026 (QA directive after the live launch) ──────────
   * Both switches are thrown: the wrapper gate is gone from mv6-buybox.liquid
   * and the qty reset is deleted from mv6-calm-rest.js. Subscribe prices per
   * tier through cards.sub (now plain `q`: both cards price the selected tier,
   * the same-day user call after seeing the live compare) — as this note promised. The history above is kept as the record of WHY the machinery was
   * mode-split; dev/check-sub-tiers.js now asserts the restored state so no
   * regeneration can quietly re-hide the tiers. */

  /* ── R-130 · EVERY CART MUTATION RUNS THROUGH ONE QUEUE ─────────────────
   * (22 Aug 2026, live report: "Your cart (6)" over a drawer showing 3.)
   * Only cadence switches were serialized; addSelection, the steppers and the
   * removes fired immediately and decided from whatever `state.cart` held —
   * null before the boot refresh landed, stale during any in-flight
   * drop-and-re-add. A click racing a re-tier saw "no subscription line",
   * added a SECOND plan line Shopify keeps separate (different plan or
   * different _evolance_* properties), and the drawer — one line per kind —
   * showed 3 bottles under a badge counting 6. The stale keys those races
   * held painted Shopify's "no valid id or line parameter".
   * Now: ONE queue for every mutation; each task starts from a FRESH /cart.js
   * read and resolves its target line at execution time, never from a
   * captured key or a captured cart. */
  var queueDepth = 0;
  var mutQueue = Promise.resolve();
  function enqueue(task) {
    queueDepth += 1;
    if (queueDepth === 1) emit();   /* busy is derived from the queue — paint the pending ring NOW */
    var run = mutQueue.then(function () { return refresh(); }).then(task);
    mutQueue = run.then(
      function () { queueDepth -= 1; if (queueDepth === 0) emit(); },
      function () { queueDepth -= 1; if (queueDepth === 0) emit(); }
    );
    return run;
  }
  function whenQueueIdle() {
    return mutQueue.then(function () { return queueDepth === 0 ? null : whenQueueIdle(); });
  }

  /* fresh-cart line resolvers — the only way a mutation may find its target */
  function targetLines(kind) {
    var items = state.cart && state.cart.items ? state.cart.items : [];
    return items.filter(function (it) {
      if (!isTargetLine(it)) return false;
      return kind === 'sub' ? !!it.selling_plan_allocation : !it.selling_plan_allocation;
    });
  }
  function lineByKey(key) {
    var items = state.cart && state.cart.items ? state.cart.items : [];
    return items.filter(function (it) { return it.key === key; })[0] || null;
  }

  /* Which cadence a selling plan id belongs to — '4' lives in DATA.plans,
     6/8 in DATA.cadences. Null for a plan we don't own (legacy contracts). */
  function cadenceOfPlan(pid) {
    if (pid == null) return null;
    var t, c;
    for (t in DATA.plans) { if (DATA.plans[t] && String(DATA.plans[t].id) === String(pid)) return '4'; }
    if (DATA.cadences) {
      for (c in DATA.cadences) {
        var col = DATA.cadences[c];
        for (t in col) { if (col[t] != null && String(col[t]) === String(pid)) return c; }
      }
    }
    return null;
  }

  /* ── R-131 · THE CART IS THE TRUTH ON EVERY IDLE READ ───────────────────
   * Adoption: on reload state.cadence booted to '4' while the cart's line sat
   * on an 8-week plan — the drawer labelled it "every 4 weeks" and the next
   * addSelection added a second line on the 4-week plan (the 6-vs-3 report's
   * likely creator). The pick now FOLLOWS the cart whenever the queue is idle.
   * Self-heal: the drawer renders ONE line per kind by contract; if an idle
   * read finds more (this bug's leftovers, or /cart's split — board item 0),
   * they are consolidated into one line at the summed, capped quantity on the
   * NEWEST line's plan. Skipped when the money path is closed — a heal
   * re-ADDS this variant (R-101); removal stays available regardless. */
  var healScheduled = false;
  var cadencePinned = false;   /* a customer pick owns the cadence for the session */
  function adoptCartState() {
    if (!cadencePinned) {
      var newestSub = targetLines('sub')[0];
      if (newestSub) {
        var c = cadenceOfPlan(actualPlanId(newestSub));
        if (c && c !== state.cadence) state.cadence = c;
      }
    }
    if (queueDepth > 0) return;
    if (!healScheduled && SELLABLE && (targetLines('sub').length > 1 || targetLines('ot').length > 1)) {
      healScheduled = true;
      console.warn('[mv6] duplicate cart lines found — consolidating to one line per kind (R-130)');
      enqueue(function () {
        var subs = targetLines('sub');
        var work = Promise.resolve();
        if (subs.length > 1) {
          var subQtySum = subs.reduce(function (n, l) { return n + l.quantity; }, 0);
          work = work.then(function () { return retierSub(subs[0].key, capSub(subQtySum)); });
        }
        work = work.then(function () {
          var ots = targetLines('ot');   /* re-read: the retier above refreshed */
          if (ots.length < 2) return null;
          var otQtySum = ots.reduce(function (n, l) { return n + l.quantity; }, 0);
          var chain = Promise.resolve();
          ots.slice(1).forEach(function (l) {
            chain = chain.then(function () { return req(DATA.routes.cart_change + '.js', { id: l.key, quantity: 0 }); });
          });
          return chain.then(function () { return setLineQty(ots[0].key, otQtySum); });
        });
        return work.then(refresh);
      }).then(function () { healScheduled = false; }, function () { healScheduled = false; });
    }
  }

  var listeners = [];
  function emit() { listeners.forEach(function (fn) { try { fn(api.state()); } catch (e) { console.error(e); } }); }

  /* Every blocked mutation answers the same way: a message the UI can paint
     (R-103) and a rejected promise, so a caller's .then never runs on a cart
     that did not change. */
  function blocked() {
    state.error = BLOCKED_MSG;
    emit();
    return Promise.reject(new Error(BLOCKED_MSG));
  }

  /* ── R-102 · nothing may outrun an in-flight cart mutation ──────────────
   * `checkout()` used to navigate unconditionally. Tap + in the drawer, then
   * Secure checkout before the /cart/change round-trip settles, and the
   * browser tears the page down mid-request: what the customer pays for is a
   * race between the network and the navigation. mv6-pending.js only disables
   * the control that was PRESSED, so the checkout button stayed live the whole
   * time. Found by an outside review, 15 Aug 2026.
   * The 8s failsafe is deliberate and matches mv6-pending.js: a lost promise
   * must never strand a customer who is trying to pay. */
  function whenIdle() {
    if (!state.busy) return Promise.resolve();
    return new Promise(function (resolve) {
      var done = false;
      function fin() { if (done) return; done = true; resolve(); }
      listeners.push(function (s) { if (!s.busy) fin(); });
      setTimeout(fin, 8000);
    });
  }

  /* ── Cart ─────────────────────────────────────────────────────────────── */
  function req(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok) throw new Error(j.description || j.message || ('Cart error ' + r.status));
        return j;
      });
    });
  }

  function refresh() {
    return fetch(DATA.routes.cart + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { state.cart = c; adoptCartState(); emit(); return c; });
  }

  /* EXECUTOR — runs inside the queue against a fresh cart. One line per kind,
     enforced at the choke point: an add that finds an existing line becomes a
     merge, because Shopify keeps a second line whenever the plan or the
     _evolance_* properties differ, and the drawer renders one per kind. */
  function add(mode, qty) {
    if (!SELLABLE) return blocked();
    var isSub = mode === 'sub';
    var existing = targetLines(isSub ? 'sub' : 'ot')[0];
    if (existing) {
      if (isSub) return retierSub(existing.key, existing.quantity + Math.max(1, qty | 0));
      return setLineQty(existing.key, existing.quantity + capOt(qty));
    }
    var q = isSub ? capSub(qty) : capOt(qty);
    var item = {
      id: DATA.variant_id,
      quantity: q,
      properties: eventProperties(mode, q)
    };

    if (isSub) {
      var plan = planId(q);
      if (!plan) {
        state.error = 'Subscription unavailable — no selling plan for ' + q + ' bottle(s).';
        emit();
        return Promise.reject(new Error(state.error));
      }
      // The subscription tier IS the quantity: plan 2 is "2 bottles, every 4
      // weeks" at qty 2. Sending qty 2 with the 1-bottle plan would charge the
      // 1-bottle rate twice and renew wrong.
      item.selling_plan = plan;
    }

    state.busy = true; state.error = null; emit();
    return req(DATA.routes.cart_add + '.js', { items: [item] })
      .then(refresh)
      .catch(function (e) { state.error = e.message; throw e; })
      .finally(function () { state.busy = false; emit(); });
  }

  function setLineQty(key, qty) {
    var q = Math.max(0, qty | 0);
    var line = state.cart && state.cart.items
      ? state.cart.items.filter(function (it) { return it.key === key; })[0]
      : null;
    var body = { id: key, quantity: q };
    if (q > 0 && isTargetLine(line)) {
      var mode = line.selling_plan_allocation ? 'sub' : 'ot';
      body.properties = mergeEventProperties(line, eventProperties(mode, q, {
        planId: actualPlanId(line)
      }));
    }
    state.busy = true; state.error = null; emit();
    return req(DATA.routes.cart_change + '.js', body)
      .then(function (c) { state.cart = c; return c; })
      .catch(function (e) { state.error = e.message; throw e; })
      .finally(function () { state.busy = false; emit(); });
  }

  /* A cart line's own selling plan decides its cap, not the mode toggle — a
   * drawer stepper on a subscription line must not pass 3.
   * Accepts either a raw cart line or one of the view objects from
   * `state().lines`, because the UI only ever holds the latter. */
  function isSubLine(line) {
    return !!(line && (line.isSub || line.selling_plan_allocation));
  }

  function lineCap(line) {
    return isSubLine(line) ? DATA.sub_max_qty : Infinity;
  }

  /* ⚠ A subscription line's quantity CANNOT be stepped — it must be re-tiered.
   * Proven on the live store: the 1-bottle plan at qty 2 charges 2 × $34.98 =
   * $69.96, where the 2-bottle tier is 2 × $32.48 = $64.96. The customer
   * overpays $5.00 and reads it as "the discount didn't apply".
   * /cart/change.js cannot change a line's selling plan, so the line is dropped
   * and re-added on the right plan. */
  function retierSub(key, qty) {
    if (qty < 1) return removeLines('sub');   /* removal is always allowed */
    var q = capSub(qty);
    if (!SELLABLE) return blocked();       /* a re-tier re-ADDS this variant */
    var plan = planId(q);
    if (!plan) {
      state.error = 'No selling plan for ' + q + ' bottle(s).';
      emit();
      return Promise.reject(new Error(state.error));
    }
    /* Drop EVERY subscription line for this variant, not just `key`: the add
       below is the one line the cart may hold afterwards (R-130) — two plan
       lines are two renewal contracts. */
    var drops = targetLines('sub');
    if (!drops.length && lineByKey(key)) drops = [lineByKey(key)];
    state.busy = true; state.error = null; emit();
    var chain = Promise.resolve();
    drops.forEach(function (l) {
      chain = chain.then(function () { return req(DATA.routes.cart_change + '.js', { id: l.key, quantity: 0 }); });
    });
    return chain
      .then(function () {
        return req(DATA.routes.cart_add + '.js', {
          items: [{
            id: DATA.variant_id,
            quantity: q,
            selling_plan: plan,
            properties: eventProperties('sub', q, { planId: plan })
          }]
        });
      })
      .then(refresh)
      .catch(function (e) { state.error = e.message; throw e; })
      .finally(function () { state.busy = false; emit(); });
  }

  /* EXECUTOR — remove every line of one kind for this variant. The drawer's
     trash can calls this (via api.removeKind): deleting "the subscription"
     must empty ALL its lines, or a hidden duplicate survives the delete —
     the live "deleted three times and the count kept counting" report. */
  function removeLines(kind) {
    var lines = targetLines(kind);
    if (!lines.length) return Promise.resolve(state.cart);
    state.busy = true; state.error = null; emit();
    var chain = Promise.resolve();
    lines.forEach(function (l) {
      chain = chain.then(function () { return req(DATA.routes.cart_change + '.js', { id: l.key, quantity: 0 }); });
    });
    return chain.then(refresh)
      .catch(function (e) { state.error = e.message; throw e; })
      .finally(function () { state.busy = false; emit(); });
  }

  /* The only stepper the drawer should call — routes by the line's own plan.
   * Stepping to 0 removes the line; a subscription line can never be stepped
   * in place, so it is dropped and re-added on the right tier's plan. */
  function stepLine(line, qty) {
    if (!line) return Promise.resolve();
    var kind = isSubLine(line) ? 'sub' : 'ot';
    var key = line.key;
    return enqueue(function () {
      var fresh = lineByKey(key) || targetLines(kind)[0];   /* keys go stale; kinds don't */
      if (!fresh) return Promise.resolve(state.cart);
      if (qty < 1) return removeLines(kind);
      return kind === 'sub' ? retierSub(fresh.key, qty) : setLineQty(fresh.key, qty);
    });
  }

  /* The drawer steppers call THIS — a delta against the cart at execution
     time, so three fast "+" taps land on +3 (queued), never on three stale
     reads of the same starting quantity. */
  function stepKind(kind, delta) {
    return enqueue(function () {
      var fresh = targetLines(kind)[0];
      if (!fresh) return Promise.resolve(state.cart);
      var next = fresh.quantity + (delta | 0);
      if (next < 1) return removeLines(kind);
      if (kind !== 'sub') return setLineQty(fresh.key, capOt(next));
      var q = capSub(next);
      if (q === fresh.quantity && actualPlanId(fresh) === planId(q)) return Promise.resolve(state.cart);
      return retierSub(fresh.key, q);
    });
  }

  /* Refresh and repair one stale target line at a time. Updating private
   * properties can change a Shopify line key or merge equivalent lines, so we
   * always continue from the cart returned by Shopify instead of retaining a
   * stale key. Checkout fails closed if the cart cannot stabilize. */
  function normalizeCheckoutLines(attempt) {
    var tries = attempt || 0;
    var items = state.cart && state.cart.items ? state.cart.items : [];
    var stale = null;
    var expected = null;

    for (var i = 0; i < items.length; i += 1) {
      if (!isTargetLine(items[i])) continue;
      var candidate = expectedLineProperties(items[i]);
      if (!hasEventContract(items[i], candidate)) {
        stale = items[i];
        expected = candidate;
        break;
      }
    }

    if (!stale) return Promise.resolve(state.cart);
    if (tries >= 12) {
      return Promise.reject(new Error('Checkout metadata could not be prepared. Please refresh and try again.'));
    }

    return req(DATA.routes.cart_change + '.js', {
      id: stale.key,
      quantity: stale.quantity,
      properties: mergeEventProperties(stale, expected)
    }).then(function (c) {
      state.cart = c;
      emit();
      return normalizeCheckoutLines(tries + 1);
    });
  }

  function prepareCheckout() {
    return whenQueueIdle()
      .then(whenIdle)
      .then(refresh)
      .then(function () { return normalizeCheckoutLines(0); });
  }

  var api = {
    data: DATA,

    state: function () {
      var q = state.qty;
      var isSub = state.mode === 'sub';
      var unit = isSub ? subUnit(q) : otUnit(q);
      var total = isSub ? subTotal(q) : otTotal(q);
      var base = DATA.compare_base * q;

      return {
        mode: state.mode,
        qty: q,
        /* busy while ANYTHING is queued, not only mid-request: mv6-pending.js
           keys the pressed control's ring on this, and the enqueue→refresh gap
           used to flash busy:false and clear the ring before it ever painted */
        busy: state.busy || queueDepth > 0,
        error: state.error,
        sellable: SELLABLE,
        cart: state.cart,

        unit: unit,
        total: total,
        compareTotal: base,
        savings: base - total,
        discountPct: pct(base, total),

        // A single one-time bottle pays list. There is no strikethrough and no
        // "% OFF" badge in that state anywhere on the page — "$49.97 / $49.97 /
        // 0% OFF / Save $0.00" reads as a rendering fault, and the bare number
        // is the message beside a Subscribe card badged 30% OFF.
        showCompare: base > total,

        planId: isSub ? planId(q) : null,
        subMaxQty: DATA.sub_max_qty,

        // The subscribe card prices what clicking it actually buys — one bottle,
        // BOTH cards price the selected tier (user call, 22 Aug 2026, on the live
        // page): with the tiles back in both modes, apples-to-apples is the point —
        // pick 3 bottles in EITHER mode and see $89.94 against $119.94. This is the
        // original pre-13-Aug behavior returning with its rationale; the interim
        // `isSub ? q : 1` existed only while subscribe could not hold a tier.
        cadence: state.cadence,
        cards: {
          sub: tierView('sub', q),
          ot: tierView('ot', q)
        },

        // The three quantity tiles, priced in the CURRENT mode.
        tiers: [tierView(state.mode, 1), tierView(state.mode, 2), tierView(state.mode, 3)],

        perServing: perServing(unit),
        servings: q * SERVINGS_PER_BOTTLE,
        capsules: q * CAPSULES_PER_BOTTLE,

        // Cart view models — the drawer renders from these and computes nothing.
        lines: lineViews(),
        subLine: splitLines().sub,
        otLine: splitLines().ot,
        totals: cartTotals(),
        upsell: upsellView(),

        money: money
      };
    },

    on: function (evt, fn) { if (evt === 'change') listeners.push(fn); return api; },

    setMode: function (m) { state.mode = m === 'ot' ? 'ot' : 'sub'; emit(); return api; },
    setQty: function (q) {
      // Cap by the CURRENT mode, but keep the value on a mode switch.
      state.qty = state.mode === 'sub' ? capSub(q) : capOt(q);
      emit();
      return api;
    },

    /* ── cadence (22 Aug 2026) ── the drawer's "Ships every N weeks" picker.
     * Fails closed: a cadence whose plan column is missing cannot be selected.
     * An existing subscription line FOLLOWS the pick immediately, through the
     * same drop-and-re-add as a tier change (a selling plan cannot be swapped
     * in place); same quantity, same price — only the renewal interval moves. */
    /* ⚠ SERIALIZED (22 Aug 2026, twice): first cadence-only, then — after the
     * live 6-vs-3 report — through the GLOBAL mutation queue (R-130), because
     * a cadence re-tier racing a stepper or the CTA was still two writers on
     * one cart. The task re-reads the cart (fresh keys), targets the CURRENT
     * pick at execution time, and skips when the line already sits on the
     * right plan — a rapid 4→6→8 collapses to one re-tier and a round-trip
     * back to the start collapses to none. */
    setCadence: function (c) {
      c = String(c || '4');
      if (c !== '4' && !(DATA.cadences && DATA.cadences[c] && DATA.cadences[c]['1'])) {
        console.error('[mv6] cadence ' + c + ' has no selling plans — refusing (R-101)');
        emit();
        return Promise.resolve();
      }
      if (state.cadence === c) { cadencePinned = true; return mutQueue; }
      cadencePinned = true;
      state.cadence = c;
      emit();
      return enqueue(function () {
        var sub = splitLines().sub;
        if (!sub) return null;
        var want = planId(sub.qty);        /* reads state.cadence NOW, not at pick time */
        if (!want || actualPlanId(sub.item) === want) return null;
        return retierSub(sub.key, sub.qty);
      }).catch(function () { /* the error slot already painted it (R-103); the queue must survive */ });
    },

    addToCart: function () { var m = state.mode, q = state.qty; return enqueue(function () { return add(m, q); }); },

    /* ⚠ THE BUY BOX MUST CALL THIS, NOT addToCart, when a cart may already
     * hold a subscription line.
     *
     * ONE SUBSCRIPTION LINE, RE-TIERED TO THE SELECTED TILE.
     *
     * This contract has flipped twice, each time with the tiles (22 Aug 2026):
     * · Originally "ONE-TIME ADDS, SUBSCRIBE SETS" — retier to `state.qty`.
     * · 14 Aug 2026, tiles HIDDEN in subscribe: the buy box invisibly reset the
     *   tier to 1, so SET shrank a 2-bottle cart to one from a button that
     *   promised nothing of the sort. The fix then: ACCUMULATE
     *   (existing + selected) — correct exactly as long as the tier is invisible.
     * · 22 Aug 2026, tiles RESTORED to subscribe: accumulate now breaks the CTA
     *   promise the other way — the tile says 2 bottles, the CTA prints $64.96,
     *   and existing(1)+selected(2) placed tier 3 at $89.94 (the post-launch QA
     *   report, and the harness's re-tier rows, which encode SET and went red).
     *   SET is back, and it is now SAFE: the selected tile is VISIBLE, the CTA
     *   label prints that tile's price, and the drawer opens on the result — a
     *   shrink can only happen because the customer explicitly picked a smaller
     *   tier they can see. The 14-Aug hazard was the invisibility, not the SET.
     *
     * Unchanged either way: only ever ONE subscription line (a second plan line
     * renews two contracts at two prices), `retierSub` drops and re-adds because
     * /cart/change.js cannot swap a selling plan, capSub holds the 3-bottle
     * ceiling, and the whole path fails closed (R-101). */
    addSelection: function () {
      return enqueue(function () {
        if (state.mode !== 'sub') return add('ot', state.qty);
        var existing = splitLines().sub;
        if (!existing) return add('sub', state.qty);
        var target = capSub(state.qty);   /* the tile the customer can SEE is the contract */
        /* Same tier AND same cadence plan: do not drop-and-re-add for no change —
           a pointless pair of round-trips and a visible flash. The enqueue's own
           refresh already re-read the cart, so the drawer opens on current state. */
        if (target === existing.qty && actualPlanId(existing.item) === planId(target)) return Promise.resolve(state.cart);
        return retierSub(existing.key, target);
      });
    },

    /* The drawer's "Upgrade & save": moves the one-time bottles onto the
     * subscription at the combined tier. Drop-then-re-add, because a line's
     * selling plan cannot be changed in place.
     * ⚠ The removal goes through `req` directly, NOT `setLineQty`. setLineQty
     * assigns `state.cart` and emits, so the UI rendered the intermediate
     * empty cart and the drawer flashed its empty state for a beat before the
     * subscription line appeared (reported from the draft theme, 12 Aug 2026).
     * One emit, at the end, from refresh(). */
    upgradeToSub: function () {
      return enqueue(function () {
        var u = upsellView();               /* recomputed from the FRESH cart */
        if (!u.show) return Promise.resolve(state.cart);
        if (!SELLABLE) return blocked();     /* drop-and-re-add is an add (R-101) */
        var drops = targetLines('ot').concat(targetLines('sub'));
        if (!drops.length) return Promise.resolve(state.cart);
        state.busy = true; state.error = null; emit();
        var chain = Promise.resolve();
        drops.forEach(function (l) {
          chain = chain.then(function () { return req(DATA.routes.cart_change + '.js', { id: l.key, quantity: 0 }); });
        });
        return chain
          .then(function () {
            return req(DATA.routes.cart_add + '.js', {
              items: [{
                id: DATA.variant_id,
                quantity: u.target,
                selling_plan: planId(u.target),
                properties: eventProperties('sub', u.target, { planId: planId(u.target) })
              }]
            });
          })
          .then(refresh)
          .catch(function (e) { state.error = e.message; throw e; })
          .finally(function () { state.busy = false; emit(); });
      });
    },
    buyNow: function () {
      // Straight to checkout, never /cart — v6 has its own drawer, so the theme's
      // cart page is a dead end that shows the OLD cart UI.
      var m = state.mode, q = state.qty;
      return enqueue(function () { return add(m, q); })
        .then(prepareCheckout)
        .then(function () { window.location.href = '/checkout'; });
    },
    checkout: function () {
      return prepareCheckout().then(function () { window.location.href = '/checkout'; });
    },
    addLine: function (mode, qty) { return enqueue(function () { return add(mode, qty); }); },
    stepLine: stepLine,
    step: stepKind,
    retierSub: function (key, qty) { return enqueue(function () { return retierSub(key, qty); }); },
    setLineQty: function (key, qty) {
      return enqueue(function () {
        if (!lineByKey(key)) return Promise.resolve(state.cart);   /* already gone — never a dead-key 404 */
        return setLineQty(key, qty);
      });
    },
    removeLine: function (key) {
      return enqueue(function () {
        if (!lineByKey(key)) return Promise.resolve(state.cart);
        return setLineQty(key, 0);
      });
    },
    removeKind: function (kind) { return enqueue(function () { return removeLines(kind); }); },
    lineCap: lineCap,
    refresh: refresh,
    normalizeCheckoutLines: function () { return refresh().then(function () { return normalizeCheckoutLines(0); }); },
    eventProperties: eventProperties,
    money: money,
    pct: pct
  };

  /* The block is a page state, not a click outcome: the buy box must read
     unavailable on first paint, not after someone presses the button. */
  if (!SELLABLE) state.error = BLOCKED_MSG;

  window.mv6 = api;
  refresh();
})();
